import { useState, useEffect, useCallback } from 'react';
import { moveDraftToSent } from '@/services/api/email';
import { Email } from '@/services/emailService';
import { useEmail } from '@/context/EmailContext';
import { useEmailConfig } from './useEmailConfig';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { textToHtml } from '@/utils/emailHtml';

interface UseEmailComposeProps {
  initialEmail?: Partial<Email>;
  isReply?: boolean;
  isReplyAll?: boolean;
  isForward?: boolean;
  onClose: () => void;
  draftId?: string;
  onSent?: () => void;
  // ponytail: lead context — when the composer was opened from a lead page
  // (via useComposeEmail.openCompose), forward these so the backend logs a
  // lead_activities row and BCCs the sender.
  leadId?: string;
  contactId?: string;
}

// Extract the bare address from "Name <email>" formatted strings
const extractEmailAddress = (addr: string): string => {
  const match = addr.match(/<([^>]+)>/);
  return (match ? match[1] : addr).trim().toLowerCase();
};

const normalizeAddressList = (list?: string[] | string): string[] => {
  if (!list) return [];
  const arr = Array.isArray(list) ? list : list.split(',');
  return arr.map(a => a.trim()).filter(Boolean);
};

interface UseEmailComposeReturn {
  toAddress: string;
  ccAddress: string;
  bccAddress: string;
  subject: string;
  bodyHtml: string;
  showCcBcc: boolean;
  isMaximized: boolean;
  isDirty: boolean;
  draftId: string | null;
  isSavingDraft: boolean;
  isSending: boolean;
  lastSaved: Date | null;
  setToAddress: (value: string) => void;
  setCcAddress: (value: string) => void;
  setBccAddress: (value: string) => void;
  setSubject: (value: string) => void;
  setBodyHtml: React.Dispatch<React.SetStateAction<string>>;
  setShowCcBcc: (show: boolean) => void;
  setIsMaximized: (maximized: boolean) => void;
  handleChange: () => void;
  handleSend: () => Promise<void>;
  handleSaveDraft: () => Promise<void>;
  handleCloseWithAutoSave: () => Promise<void>;
}

export const useEmailCompose = ({
  initialEmail,
  isReply = false,
  isReplyAll = false,
  isForward = false,
  onClose,
  draftId: initialDraftId,
  onSent,
  leadId,
  contactId,
}: UseEmailComposeProps): UseEmailComposeReturn => {
  const { sendEmail, replyToEmail, saveDraft, loadDraft, sendDraft } = useEmail();
  const { emailConfig, loading: configLoading } = useEmailConfig();
  const { user } = useAuth();
  const { toast } = useToast();

  const isReplyMode = isReply || isReplyAll;

  // Addresses that belong to the current user — excluded from Reply All
  const getOwnAddresses = (): string[] => [
    emailConfig?.email,
    (emailConfig as any)?.imapEmail,
    (user as any)?.imap_email,
    user?.email,
  ].filter(Boolean).map(e => extractEmailAddress(String(e)));

  // Reply → sender only. Reply All → sender + original To, original Cc stays
  // in Cc — excluding the user's own addresses and duplicates.
  // Replying to your OWN sent message targets the original recipients instead.
  const computeReplyRecipients = (replyAll: boolean): { to: string; cc: string } => {
    if (!initialEmail) return { to: '', cc: '' };
    const own = getOwnAddresses();
    const isOwn = (addr: string) => own.includes(extractEmailAddress(addr));

    const sender = (initialEmail.from || '').trim();
    const originalTo = normalizeAddressList(initialEmail.to);
    const originalCc = normalizeAddressList(initialEmail.cc);

    if (!replyAll) {
      if (sender && isOwn(sender)) {
        return { to: originalTo.filter(a => !isOwn(a)).join(', '), cc: '' };
      }
      return { to: sender, cc: '' };
    }

    const seen = new Set<string>();
    const toList: string[] = [];
    const pushTo = (addr: string) => {
      const key = extractEmailAddress(addr);
      if (!key || seen.has(key) || isOwn(addr)) return;
      seen.add(key);
      toList.push(addr);
    };
    if (sender) pushTo(sender);
    originalTo.forEach(pushTo);

    const ccList: string[] = [];
    originalCc.forEach(addr => {
      const key = extractEmailAddress(addr);
      if (!key || seen.has(key) || isOwn(addr)) return;
      seen.add(key);
      ccList.push(addr);
    });

    return { to: toList.join(', '), cc: ccList.join(', ') };
  };

  const replyRecipients = isReplyMode ? computeReplyRecipients(isReplyAll) : null;

  const [toAddress, setToAddress] = useState<string>(
    replyRecipients ? replyRecipients.to : (initialEmail?.to?.join(', ') || '')
  );
  const [ccAddress, setCcAddress] = useState<string>(
    replyRecipients ? replyRecipients.cc : (initialEmail?.cc?.join(', ') || '')
  );
  const [bccAddress, setBccAddress] = useState<string>(initialEmail?.bcc?.join(', ') || '');
  const [subject, setSubject] = useState<string>(initialEmail?.subject || '');
  // Prefill: prefer HTML body; fall back to converting a plain-text body
  // (e.g. an AI draft or a simple prefill from another feature) to HTML.
  const [bodyHtml, setBodyHtml] = useState<string>(
    initialEmail?.body_html || (initialEmail?.body ? textToHtml(initialEmail.body) : '')
  );
  const [showCcBcc, setShowCcBcc] = useState<boolean>(
    Boolean(replyRecipients ? replyRecipients.cc.length > 0 : (initialEmail?.cc && initialEmail.cc.length > 0)) ||
    Boolean(initialEmail?.bcc && initialEmail.bcc.length > 0)
  );
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [draftId, setDraftId] = useState<string | null>(initialDraftId || null);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Load draft if draftId is provided
  useEffect(() => {
    if (initialDraftId && loadDraft) {
      const loadExistingDraft = async () => {
        try {
          const draft = await loadDraft(initialDraftId);

          // Set all fields from the loaded draft
          if (draft) {
            setToAddress(Array.isArray(draft.to) ? draft.to.join(', ') : draft.to || '');
            setCcAddress(Array.isArray(draft.cc) ? draft.cc.join(', ') : draft.cc || '');
            setBccAddress(Array.isArray(draft.bcc) ? draft.bcc.join(', ') : draft.bcc || '');
            setSubject(draft.subject || '');
            setBodyHtml(draft.body_html || (draft.body ? textToHtml(draft.body) : ''));
            setShowCcBcc((draft.cc && draft.cc.length > 0) || (draft.bcc && draft.bcc.length > 0));
            setIsDirty(false);
          }
        } catch (error) {
          console.error('Failed to load draft:', error);
        }
      };
      loadExistingDraft();
    }
  }, [initialDraftId, loadDraft]);

  // Auto-save functionality
  useEffect(() => {
    if (!isDirty) return;

    const autoSaveTimer = setTimeout(() => {
      handleSaveDraft();
    }, 30000); // Auto-save every 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [isDirty, toAddress, ccAddress, bccAddress, subject, bodyHtml]);

  useEffect(() => {
    if (isReplyMode && initialEmail) {
      let replyHtml = `<p><br></p><p><br></p><div style="border-left: 2px solid #ccc; padding-left: 1rem; margin: 1rem 0;">
        <p>On ${new Date(initialEmail.date).toLocaleString()}, ${initialEmail.from_name || initialEmail.from} wrote:</p>
        ${initialEmail.body_html || initialEmail.body}
      </div>`;
      
      setBodyHtml(replyHtml);
      setSubject(subject.startsWith('Re: ') ? subject : `Re: ${subject}`);
    } else if (isForward && initialEmail) {
      let forwardHtml = `<p><br></p><p><br></p><div style="border: 1px solid #ccc; padding: 10px; margin: 10px 0;">
        <p><strong>---------- Forwarded message ----------</strong></p>
        <p><strong>From:</strong> ${initialEmail.from_name || initialEmail.from}</p>
        <p><strong>Date:</strong> ${new Date(initialEmail.date).toLocaleString()}</p>
        <p><strong>Subject:</strong> ${initialEmail.subject}</p>
        <p><strong>To:</strong> ${initialEmail.to.join(', ')}</p>
      </div>
      ${initialEmail.body_html || initialEmail.body}`;

      setBodyHtml(forwardHtml);
      setSubject(subject.startsWith('Fwd: ') ? subject : `Fwd: ${subject}`);
    }
  }, [initialEmail, isReplyMode, isForward]);

  const handleChange = useCallback(() => {
    setIsDirty(true);
  }, []);

  const handleSend = async () => {
    if (!toAddress.trim()) {
      toast({
        title: "Missing Recipient",
        description: "Please enter at least one recipient",
        variant: "destructive",
      });
      return;
    }

    if (!emailConfig && !user) {
      console.error('📧 No email config or user data:', { emailConfig, user });
      toast({
        title: "Configuration Error", 
        description: "Email configuration not loaded. Please wait and try again.",
        variant: "destructive",
      });
      return;
    }

    // Use email config if available, otherwise fallback to user profile
    const fromEmail = emailConfig?.email || (user as any)?.imap_email || user?.email || '';
    const fromName = emailConfig?.fullName || (user as any)?.email_full_name || `${user?.firstName} ${user?.lastName}`.trim() || '';
    
    console.log('📧 Using email credentials:', { fromEmail, fromName });

    console.log('📧 Setting isSending to true');
    setIsSending(true);
    
    try {
      // Plain Reply uses the threaded reply endpoint (it delivers to the
      // sender server-side). Reply All falls through to the regular send
      // endpoint so every recipient actually receives the message.
      const isReplyToEmail = isReply && !isReplyAll && initialEmail?.id && replyToEmail;
      
      if (isReplyToEmail) {
        console.log('📧 Sending reply to email:', initialEmail.id);
        
        // Use the reply endpoint
        await replyToEmail(initialEmail.id, {
          body: bodyHtml.replace(/<[^>]*>/g, '') || ' ',
          body_html: bodyHtml || ' '
        });
        
        setIsDirty(false);
        
        toast({
          title: "Reply Sent",
          description: "Your reply has been sent successfully!",
        });
        
        // Refresh inbox and sent folders
        if (onSent) onSent();
        else onClose();
        return;
      }
      
      // If this is an existing draft, send it as a draft
      if (draftId) {
        console.log('📧 Sending existing draft:', draftId);
        
        // For drafts, we need to get the draft content and send it as a regular email
        const email = {
          from_address: fromEmail,
          from_name: fromName,
          to: toAddress.split(',').map(email => email.trim()).filter(email => email.length > 0),
          cc: ccAddress ? ccAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
          bcc: bccAddress ? bccAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
          subject: subject.trim(),
          body: bodyHtml.replace(/<[^>]*>/g, '') || ' ',  // Ensure body is never empty
          body_html: bodyHtml || ' ',  // Ensure body_html is never empty
          attachments: [],
          // ponytail: lead context → backend logs lead_activities + BCCs sender
          source: leadId ? 'lead' : undefined,
          lead_id: leadId,
          lead_contact_id: contactId,
        };

        console.log('📧 Final draft email data being sent:', email);
        await sendEmail(email as any);
        
        // Move the draft to sent folder
        try {
          await moveDraftToSent(draftId);
          console.log('📧 Draft moved to sent folder successfully');
        } catch (moveError) {
          console.error('💥 Failed to move draft to sent folder:', moveError);
          // Don't fail the send operation if move fails
        }
        
        setIsDirty(false);
        
        toast({
          title: "Email Sent",
          description: "Your draft email has been sent successfully!",
        });
        
        if (onSent) {
          onSent(); // Call the onSent callback for navigation
        } else {
          onClose();
        }
        return;
      }

      // Otherwise, send as a new email (also used by Reply All so every
      // recipient receives the message; thread metadata keeps it threaded)
      const email = {
        from_address: fromEmail,
        from_name: fromName,
        to: toAddress.split(',').map(email => email.trim()).filter(email => email.length > 0),
        cc: ccAddress ? ccAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
        bcc: bccAddress ? bccAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
        subject: subject.trim(),
        body: bodyHtml.replace(/<[^>]*>/g, '') || ' ',  // Strip HTML for plain text, ensure not empty
        body_html: bodyHtml || ' ',  // Ensure not empty
        thread_id: initialEmail?.thread_id || '',
        reply_to: isReplyMode ? initialEmail?.id : '',
        attachments: [],
        // ponytail: lead context → backend logs lead_activities + BCCs sender
        source: leadId ? 'lead' : undefined,
        lead_id: leadId,
        lead_contact_id: contactId,
      };

      console.log('📧 Final new email data being sent:', email);

      await sendEmail(email as any);
      setIsDirty(false);

      toast({
        title: isReplyAll ? "Reply Sent" : "Email Sent",
        description: isReplyAll
          ? "Your reply has been sent to all recipients!"
          : "Your email has been sent successfully!",
      });
      
      if (onSent) {
        onSent(); // Call the onSent callback for navigation
      } else {
        onClose();
      }
    } catch (error) {
      console.error('💥 Failed to send email:', error);
      toast({
        title: "Send Failed",
        description: `Failed to send email: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      console.log('📧 Setting isSending to false');
      setIsSending(false);
    }
  };

  const hasContent = useCallback(() => {
    // Check if any meaningful content exists
    const hasToAddress = toAddress.trim().length > 0;
    const hasSubject = subject.trim().length > 0;
    
    // Strip HTML tags and check for actual text content
    const textContent = bodyHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const hasBodyContent = textContent.length > 0;
    
    return hasToAddress || hasSubject || hasBodyContent;
  }, [toAddress, subject, bodyHtml]);

  const handleSaveDraft = async () => {
    if (!emailConfig) {
      console.warn('Email config not loaded, cannot save draft');
      return;
    }

    setIsSavingDraft(true);
    
    const draftEmail = {
      from: emailConfig.email || '',
      from_name: emailConfig.fullName || '',
      to: toAddress ? toAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
      cc: ccAddress ? ccAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
      bcc: bccAddress ? bccAddress.split(',').map(email => email.trim()).filter(email => email.length > 0) : [],
      subject: subject.trim(),
      body: bodyHtml.replace(/<[^>]*>/g, ''),  // Strip HTML for plain text
      body_html: bodyHtml,
      thread_id: initialEmail?.thread_id || '',
      reply_to: isReplyMode ? initialEmail?.id : ''
    };

    try {
      const savedDraftId = await saveDraft(draftEmail, draftId);
      setDraftId(savedDraftId);
      setIsDirty(false);
      setLastSaved(new Date());
      console.log('📧 Draft saved successfully:', savedDraftId);
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleCloseWithAutoSave = useCallback(async () => {
    // Auto-save if there's content and the form is dirty
    if (hasContent() && isDirty && emailConfig && !isSending) {
      try {
        console.log('📧 Auto-saving draft before close');
        await handleSaveDraft();
      } catch (error) {
        console.error('💥 Auto-save failed on close:', error);
        // Don't block close if auto-save fails
      }
    }
    onClose();
  }, [hasContent, isDirty, emailConfig, isSending, handleSaveDraft, onClose]);

  return {
    toAddress,
    ccAddress,
    bccAddress,
    subject,
    bodyHtml,
    showCcBcc,
    isMaximized,
    isDirty,
    draftId,
    isSavingDraft,
    isSending,
    lastSaved,
    setToAddress,
    setCcAddress,
    setBccAddress,
    setSubject,
    setBodyHtml,
    setShowCcBcc,
    setIsMaximized,
    handleChange,
    handleSend,
    handleSaveDraft,
    handleCloseWithAutoSave
  };
};
